import { getOrCreateATA, TOKEN_PROGRAM_ID } from "@saberhq/token-utils";
import { SystemProgram, SYSVAR_CLOCK_PUBKEY, SYSVAR_RENT_PUBKEY, } from "@solana/web3.js";
import { BN } from "bn.js";
const ZERO = new BN(0);
export class LockupWrapper {
    constructor(saber) {
        this.saber = saber;
        this.program = saber.programs.Lockup;
    }
    get provider() {
        return this.saber.provider;
    }
    async releaseAddress(beneficiary) {
        return await this.program.account.release.associatedAddress(beneficiary);
    }
    async fetchRelease(beneficiary) {
        const key = await this.releaseAddress(beneficiary);
        const data = await this.provider.connection.getAccountInfo(key);
        if (!data) {
            return null;
        }
        return this.program.coder.accounts.decode("Release", data.data);
    }
    createRelease({ amount, startTs, endTs, beneficiary, release, minterInfo, mint, }) {
        const minterAddIx = this.saber.programs.MintProxy.state.instruction.minterAdd(amount, {
            accounts: {
                auth: { owner: this.provider.wallet.publicKey },
                minter: release,
                minterInfo,
                payer: this.provider.wallet.publicKey,
                systemProgram: SystemProgram.programId,
                rent: SYSVAR_RENT_PUBKEY,
            },
        });
        const createReleaseIx = this.program.state.instruction.createRelease(amount, startTs, endTs, {
            accounts: {
                minterInfo,
                mint,
                auth: { owner: this.provider.wallet.publicKey },
                beneficiary,
                release,
                payer: this.provider.wallet.publicKey,
                systemProgram: SystemProgram.programId,
                rent: SYSVAR_RENT_PUBKEY,
                mintProxyProgram: this.saber.programs.MintProxy.programId,
            },
        });
        return {
            release,
            tx: this.saber.newTx([minterAddIx, createReleaseIx]),
        };
    }
    /**
     * Creates a release for a beneficiary.
     */
    async createReleaseForBeneficiary({ amount, startTs, endTs, beneficiary, }) {
        const release = await this.releaseAddress(beneficiary);
        const minterInfo = await this.saber.mintProxy.getMinterInfoAddress(release);
        const mintProxyStateData = await this.saber.programs.MintProxy.state.fetch();
        return this.createRelease({
            amount,
            startTs,
            endTs,
            release,
            beneficiary,
            minterInfo,
            mint: mintProxyStateData.tokenMint,
        });
    }
    /**
     * Withdraws tokens.
     * @param beneficiary
     * @returns
     */
    async withdraw(beneficiary = this.saber.provider.wallet.publicKey, amount = ZERO) {
        const mintProxyStateAddress = this.saber.mintProxy.program.state.address();
        const mintProxyState = await this.saber.mintProxy.program.state.fetch();
        const instructions = [];
        const { address, instruction } = await getOrCreateATA({
            provider: this.saber.provider,
            mint: mintProxyState.tokenMint,
            owner: beneficiary,
        });
        if (instruction) {
            instructions.push(instruction);
        }
        const release = await this.releaseAddress(beneficiary);
        const accounts = {
            proxyMintAuthority: mintProxyState.proxyMintAuthority,
            tokenMint: mintProxyState.tokenMint,
            beneficiary,
            release,
            tokenAccount: address,
            tokenProgram: TOKEN_PROGRAM_ID,
            unusedClock: SYSVAR_CLOCK_PUBKEY,
            minterInfo: await this.saber.mintProxy.getMinterInfoAddress(release),
            mintProxyState: mintProxyStateAddress,
            mintProxyProgram: this.saber.programs.MintProxy.programId,
        };
        if (amount.isZero()) {
            instructions.push(this.program.state.instruction.withdraw({
                accounts,
            }));
        }
        else {
            instructions.push(this.program.state.instruction.withdrawWithAmount(amount, { accounts }));
        }
        return this.saber.newTx(instructions);
    }
    /**
     * Creates a release for a beneficiary.
     */
    async revokeReleaseForBeneficiary({ beneficiary, }) {
        const release = await this.releaseAddress(beneficiary);
        const minterInfo = await this.saber.mintProxy.getMinterInfoAddress(release);
        const minterRemoveIx = this.saber.programs.MintProxy.state.instruction.minterRemove({
            accounts: {
                auth: { owner: this.provider.wallet.publicKey },
                minter: release,
                minterInfo,
                payer: this.provider.wallet.publicKey,
            },
        });
        const revokeReleaseIx = this.program.state.instruction.revokeRelease({
            accounts: {
                auth: { owner: this.provider.wallet.publicKey },
                release,
                payer: this.provider.wallet.publicKey,
            },
        });
        return {
            release,
            tx: this.saber.newTx([minterRemoveIx, revokeReleaseIx]),
        };
    }
}
//# sourceMappingURL=lockup.js.map